import { create } from 'zustand';
import { AppNode, Connection, NodeStatus, NodeType } from '../types';
import type { Editor } from '@tiptap/react';

// 编辑器实例缓存（不参与zustand响应式，避免频繁重渲染）
const editorCache = new Map<string, Editor>();
export const getEditorInstance = (nodeId: string) => editorCache.get(nodeId);
export const setEditorInstance = (nodeId: string, editor: Editor | null) => {
  if (editor) {
    editorCache.set(nodeId, editor);
  } else {
    editorCache.delete(nodeId);
  }
};

export interface NodeState {
  nodes: AppNode[];
  connections: Connection[];
  nodeMap: Map<string, AppNode>;
  addNode: (node: AppNode) => void;
  updateNode: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string, status?: NodeStatus) => void;
  deleteNodes: (ids: string[]) => void;
  addConnection: (from: string, to: string) => void;
  removeConnection: (from: string, to: string) => void;
  setNodes: (nodes: AppNode[]) => void;
  setConnections: (connections: Connection[]) => void;
  duplicateNode: (id: string) => AppNode | null;
  _updateNodePosition: (id: string, x: number, y: number) => void;
  _updateNodeGroupId: (id: string, groupId: string | undefined) => void;
  _updateNodeParent: (id: string, parentNode: string | undefined, extent?: AppNode['extent'], expandParent?: boolean) => void;
  _batchUpdateNodes: (updates: Array<{ id: string; changes: Partial<AppNode> }>) => void;
  // P2-1: 局部更新节点位置，保持未变动节点引用不变
  // 之前 setNodes(finalNodes) 会创建新数组，导致 useInputAssets 的 useShallow 判定不等，
  // 触发所有 GenerateNode 重渲染。此方法只更新被拖拽节点，未拖拽节点引用不变。
  updateNodePositions: (updates: Array<{ id: string; x: number; y: number }>) => void;
  updateNodeDataField: (id: string, field: string, value: unknown) => void;
  updateNodesBatch: (updates: Array<{ id: string; data: Partial<AppNode['data']> }>) => void;
  _removeNodesByGroupId: (groupId: string) => void;
  getNodeById: (id: string) => AppNode | undefined;
  getNodesByGroupId: (groupId: string) => AppNode[];
  getNodesInBounds: (x: number, y: number, width: number, height: number) => AppNode[];

  // 文本节点专用方法
  updateNodeSubMode: (id: string, subMode: import('../types').PromptSubMode) => void;
  updateNodeEditMode: (id: string, editMode: import('../types').TextEditMode) => void;
  updateNodeSize: (id: string, size: import('../types').NodeSize) => void;
  updateNodeRichContent: (id: string, richContent: import('../types').RichTextContent, plainText: string) => void;
  getNextTextNodeIndex: () => number;
}

function rebuildNodeMap(nodes: AppNode[]): Map<string, AppNode> {
  const map = new Map<string, AppNode>();
  for (const n of nodes) {
    map.set(n.id, n);
  }
  return map;
}

export const useNodeStore = create<NodeState>((set, get) => ({
  nodes: [],
  connections: [],
  nodeMap: new Map(),

  addNode: (node) => set((state) => {
    if (!state?.nodes) return state;
    const newNodes = [...state.nodes, node];
    const newMap = new Map(state.nodeMap);
    newMap.set(node.id, node);
    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNode: (id, data, size, title, status) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, data: { ...oldNode.data, ...data }, title: title || oldNode.title };
    if (size?.width) updated.width = size.width;
    if (size?.height) updated.height = size.height;
    if (status) updated.status = status;

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  deleteNodes: (ids) => {
    set((state) => {
      if (!state?.nodes) return state;
      const idSet = new Set(ids);
      const newNodes = state.nodes.filter((n) => !idSet.has(n.id));
      const newMap = rebuildNodeMap(newNodes);
      return {
        nodes: newNodes,
        nodeMap: newMap,
        connections: state.connections.filter((c) => !idSet.has(c.from) && !idSet.has(c.to)),
      };
    });
  },

  addConnection: (from, to) => set((state) => {
    if (!state?.nodes) return state;
    if (state.connections.some((c) => c.from === from && c.to === to)) {
      return state;
    }

    const targetIndex = state.nodes.findIndex(n => n.id === to);
    if (targetIndex === -1) {
      return {
        connections: [...state.connections, { from, to }],
      };
    }

    const targetNode = state.nodes[targetIndex];
    const updatedTarget = { ...targetNode, inputs: [...targetNode.inputs, from] };
    if (targetNode.type === NodeType.IMAGE_GENERATOR && (!targetNode.data.generationMode || targetNode.data.generationMode === 'TXT2IMG')) {
      updatedTarget.data = { ...updatedTarget.data, generationMode: 'IMG2IMG' };
    }

    const newNodes = [...state.nodes];
    newNodes[targetIndex] = updatedTarget;

    const newMap = new Map(state.nodeMap);
    newMap.set(to, updatedTarget);

    return {
      connections: [...state.connections, { from, to }],
      nodes: newNodes,
      nodeMap: newMap,
    };
  }),

  removeConnection: (from, to) => set((state) => {
    if (!state?.nodes) return state;
    const targetIndex = state.nodes.findIndex(n => n.id === to);
    if (targetIndex === -1) {
      return {
        connections: state.connections.filter((c) => c.from !== from || c.to !== to),
      };
    }

    const targetNode = state.nodes[targetIndex];
    const updatedTarget = { ...targetNode, inputs: targetNode.inputs.filter((i) => i !== from) };

    const newNodes = [...state.nodes];
    newNodes[targetIndex] = updatedTarget;

    const newMap = new Map(state.nodeMap);
    newMap.set(to, updatedTarget);

    return {
      connections: state.connections.filter((c) => c.from !== from || c.to !== to),
      nodes: newNodes,
      nodeMap: newMap,
    };
  }),

  setNodes: (nodes) => set({ nodes, nodeMap: rebuildNodeMap(nodes) }),
  setConnections: (connections) => set({ connections }),

  duplicateNode: (id) => {
    const state = get();
    const node = state.nodeMap.get(id);
    if (!node) return null;

    const newNode: AppNode = {
      ...node,
      id: `${node.id}-copy-${Date.now()}`,
      x: node.x + 50,
      y: node.y + 50,
      inputs: [],
    };

    const newNodes = [...state.nodes, newNode];
    const newMap = new Map(state.nodeMap);
    newMap.set(newNode.id, newNode);
    set({ nodes: newNodes, nodeMap: newMap });
    return newNode;
  },

  _updateNodePosition: (id, x, y) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const updated = { ...state.nodes[index], x, y };
    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  _updateNodeGroupId: (id, groupId) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const updated = { ...state.nodes[index], groupId };
    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  _updateNodeParent: (id, parentNode, extent, expandParent) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const updated = { ...state.nodes[index], parentNode, extent, expandParent };
    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  _batchUpdateNodes: (updates) => set((state) => {
    if (!state?.nodes) return state;
    const updateMap = new Map(updates.map(u => [u.id, u.changes]));
    let changed = false;
    const newNodes = state.nodes.map(n => {
      const changes = updateMap.get(n.id);
      if (changes) {
        changed = true;
        return { ...n, ...changes };
      }
      return n;
    });

    if (!changed) return state;

    const newNodeMap = rebuildNodeMap(newNodes);
    return { nodes: newNodes, nodeMap: newNodeMap };
  }),

  // P2-1: 局部更新节点位置，只替换被拖拽节点的对象引用
  // 未拖拽节点保持原引用，useShallow 比较时判定相等，不触发重渲染
  updateNodePositions: (updates) => set((state) => {
    if (!state?.nodes || updates.length === 0) return state;
    const updateMap = new Map(updates.map(u => [u.id, { x: u.x, y: u.y }]));
    let changed = false;
    const newNodes = state.nodes.map(n => {
      const pos = updateMap.get(n.id);
      if (pos && (n.x !== pos.x || n.y !== pos.y)) {
        changed = true;
        return { ...n, x: pos.x, y: pos.y };
      }
      return n;
    });

    if (!changed) return state;

    // 只更新被拖拽节点的 map 条目，避免全量重建
    const newMap = new Map(state.nodeMap);
    for (const update of updates) {
      const node = newMap.get(update.id);
      if (node && (node.x !== update.x || node.y !== update.y)) {
        newMap.set(update.id, { ...node, x: update.x, y: update.y });
      }
    }
    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodeDataField: (id, field, value) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    if ((oldNode.data as Record<string, unknown>)[field] === value) return state;

    const updated = { ...oldNode, data: { ...oldNode.data, [field]: value } };
    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodesBatch: (updates) => set((state) => {
    if (!state?.nodes) return state;
    const updateMap = new Map(updates.map(u => [u.id, u.data]));
    let changed = false;
    const newNodes = state.nodes.map(n => {
      const dataUpdate = updateMap.get(n.id);
      if (dataUpdate) {
        changed = true;
        return { ...n, data: { ...n.data, ...dataUpdate } };
      }
      return n;
    });

    if (!changed) return state;

    const newNodeMap = rebuildNodeMap(newNodes);
    return { nodes: newNodes, nodeMap: newNodeMap };
  }),

  _removeNodesByGroupId: (groupId) => set((state) => {
    if (!state?.nodes) return state;
    const newNodes = state.nodes.filter(n => n.groupId !== groupId);
    return { nodes: newNodes, nodeMap: rebuildNodeMap(newNodes) };
  }),

  getNodeById: (id) => get().nodeMap.get(id),

  getNodesByGroupId: (groupId) => get().nodes.filter(n => n.groupId === groupId),

  getNodesInBounds: (x, y, width, height) => {
    return get().nodes.filter(n => {
      const nw = n.width ?? 280;
      const nh = n.height ?? 200;
      return n.x >= x && n.x + nw <= x + width && n.y >= y && n.y + nh <= y + height;
    });
  },

  // 文本节点专用方法
  updateNodeSubMode: (id, subMode) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, data: { ...oldNode.data, subMode } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodeEditMode: (id, editMode) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, data: { ...oldNode.data, editMode } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodeSize: (id, size) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, width: size.width, height: size.height, data: { ...oldNode.data, nodeSize: size } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  updateNodeRichContent: (id, richContent, plainText) => set((state) => {
    if (!state?.nodes) return state;
    const index = state.nodes.findIndex(n => n.id === id);
    if (index === -1) return state;

    const oldNode = state.nodes[index];
    const updated = { ...oldNode, data: { ...oldNode.data, richContent, textContent: plainText } };

    const newNodes = [...state.nodes];
    newNodes[index] = updated;

    const newMap = new Map(state.nodeMap);
    newMap.set(id, updated);

    return { nodes: newNodes, nodeMap: newMap };
  }),

  getNextTextNodeIndex: () => {
    const state = get();
    const textNodes = state.nodes.filter(n => n.data.textNodeIndex !== undefined);
    const maxIndex = textNodes.length > 0
      ? Math.max(...textNodes.map(n => n.data.textNodeIndex || 0))
      : 0;
    return maxIndex + 1;
  },
}));

if (import.meta.hot) {
  import.meta.hot.accept();
}
